import { Config } from "@planetscale/database";
import { select } from "gamedata/GameData";
import { ItemLink } from "~/components/item/ItemLink";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { Icon } from "~/components/ui/Icon";
import { PageTitle } from "~/components/ui/PageTitle";
import { Table } from "~/components/ui/Table";
import { GD_ALL } from "~/gd";
import { gameName } from "~/util/common";
import { lstring, tt, useT, useTurl } from "~/util/Lang";
import { $LOADER, $PAGE, MetaArgs, OurLoaderArgs } from "~/util/Loaders";
import { logPageView } from "~/util/stats";
import { homePage } from "~/util/titles";

async function makeData(la: OurLoaderArgs, cfg: Config) {
  // Log
  logPageView(la);
  //
  const t = tt.bind(null, la.realm.lang);

  const all = Array.from(GD_ALL[la.realm.game].sets.values());

  const sets = select(all, {
    select: {
      id: true,
      setName: true,
      icon: true,
      name: t,
      desc: t,
      items: {
        slotHead: {
          select: { id: true, itemName: true, name: t, icon: true },
        },
        slotChest: {
          select: { id: true, itemName: true, name: t, icon: true },
        },
        slotGloves: {
          select: { id: true, itemName: true, name: t, icon: true },
        },
        slotLegs: {
          select: { id: true, itemName: true, name: t, icon: true },
        },
        slotFeet: {
          select: { id: true, itemName: true, name: t, icon: true },
        },
        slotLhand: {
          select: { id: true, itemName: true, name: t, icon: true },
        },
      },
    },
  });

  return { sets, authUser: la.req.sess?.user };
}

type LoaderData = Awaited<ReturnType<typeof makeData>>;

function makeMeta(
  cx: MetaArgs<LoaderData>,
  opts: { title: lstring; canonicalPath?: string }
) {
  return {
    title: `${cx.t({ en: "Item Sets", ru: "Сеты" })} - ${homePage(cx.realm)}`,
    description: cx.t({
      en: `${gameName(cx.realm.game)} Item Sets`,
      ru: `Сеты ${gameName(cx.realm.game)}`,
    }),
    ogTitle: `Lineage 2 Item Sets`,
  };
}

$PAGE({
  path: "items/sets",
  loader: $LOADER?.((la) => {
    return makeData(la, {});
  }),
  meta: (cx) =>
    makeMeta(cx, {
      title: { en: "Weapons", ru: "Оружие" },
      canonicalPath: "/items/weapon",
    }),
  render: ({ data }) => {
    const t = useT();
    const turl = useTurl();
    const setsTitle = t({ en: "Items Sets", ru: "Сеты" });

    return (
      <div>
        <div className="content">
          <Breadcrumb items={[{ title: setsTitle, url: turl("/sets") }]} />
          <PageTitle>{setsTitle}</PageTitle>
        </div>
        <div style={{ overflowX: "auto" }}>
          <Table
            data={data.sets}
            id={(set) => set.id}
            vAlign="top"
            cols={[
              {
                title: t({ en: "Name", ru: "Имя" }),
                cell: (set) => (
                  <div
                    style={{
                      display: "flex",
                      columnGap: "10px",
                      flexDirection: "row",
                    }}
                  >
                    <div style={{ marginTop: "5px" }}>
                      <Icon icon={set.icon} />
                    </div>
                    <div>
                      <div style={{ color: "#fff", fontWeight: "bold" }}>
                        {set.name}
                      </div>
                      <div style={{ fontSize: "14px" }}>{set.desc}</div>
                    </div>
                  </div>
                ),
              },
              {
                title: t({ en: "Items", ru: "Предметы" }),
                cell: (set) => (
                  <div>
                    {set.items.slotHead.map((item, i) => (
                      <div key={i} style={{ paddingBottom: "5px" }}>
                        <ItemLink item={item} />
                      </div>
                    ))}
                    {set.items.slotChest.map((item, i) => (
                      <div key={i} style={{ paddingBottom: "5px" }}>
                        <ItemLink item={item} />
                      </div>
                    ))}
                    {set.items.slotLegs.map((item, i) => (
                      <div key={i} style={{ paddingBottom: "5px" }}>
                        <ItemLink item={item} />
                      </div>
                    ))}
                    {set.items.slotGloves.map((item, i) => (
                      <div key={i} style={{ paddingBottom: "5px" }}>
                        <ItemLink item={item} />
                      </div>
                    ))}
                    {set.items.slotFeet.map((item, i) => (
                      <div key={i} style={{ paddingBottom: "5px" }}>
                        <ItemLink item={item} />
                      </div>
                    ))}
                    {set.items.slotLhand.map((item, i) => (
                      <div key={i} style={{ paddingBottom: "5px" }}>
                        <ItemLink item={item} />
                      </div>
                    ))}
                  </div>
                ),
              },
            ]}
          />
        </div>
      </div>
    );
  },
});
